import { useEffect, useRef, useState } from 'react';

type State = {
  isIntersecting: boolean;
  entry?: IntersectionObserverEntry;
};

type UseIntersectionObserverOptions = {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  freezeOnceVisible?: boolean;
  onChange?: (
    isIntersecting: boolean,
    entry: IntersectionObserverEntry
  ) => void;
  initialIsIntersecting?: boolean;
};

type IntersectionReturn = [
  (node?: Element | null) => void,
  boolean,
  IntersectionObserverEntry | undefined
] & {
  ref: (node?: Element | null) => void;
  isIntersecting: boolean;
  entry?: IntersectionObserverEntry;
};

/**
 * useIntersectionObserver - кастомный React-хук для отслеживания пересечения элемента с границами видимости (viewport или контейнера)
 * с использованием Intersection Observer API.
 *
 * https://github.com/juliencrn/usehooks-ts/blob/master/packages/usehooks-ts/src/useIntersectionObserver/useIntersectionObserver.ts
 *
 * @param {Object} options - Настройки для `IntersectionObserver`.
 * @param {Element | Document | null} [options.root=null] - Корневой элемент для определения пересечения. По умолчанию это viewport.
 * @param {string} [options.rootMargin='0%'] - Отступы вокруг корневого элемента для увеличения или уменьшения области видимости.
 * @param {number | number[]} [options.threshold=0] - Число или массив чисел для указания порога пересечения в диапазоне от 0 до 1.
 * @param {boolean} [options.freezeOnceVisible=false] - Опция для остановки отслеживания после того, как элемент станет видимым.
 * @param {boolean} [options.initialIsIntersecting=false] - Начальное значение флага видимости элемента.
 * @param {(isIntersecting: boolean, entry: IntersectionObserverEntry) => void} [options.onChange] - Колбэк, вызываемый при изменении состояния пересечения.
 *
 * @returns {IntersectionReturn} - Массив и объект, содержащие:
 * - `ref` - функция для назначения элемента для отслеживания.
 * - `isIntersecting` - флаг, указывающий, пересекается ли элемент с областью видимости.
 * - `entry` - объект `IntersectionObserverEntry`, содержащий информацию о пересечении.
 */

export function useIntersectionObserver({
  threshold = 0,
  root = null,
  rootMargin = '0%',
  freezeOnceVisible = false,
  initialIsIntersecting = false,
  onChange,
}: UseIntersectionObserverOptions = {}): IntersectionReturn {
  const [ref, setRef] = useState<Element | null>(null);

  const [state, setState] = useState<State>(() => ({
    isIntersecting: initialIsIntersecting,
    entry: undefined,
  }));

  const callbackRef = useRef<UseIntersectionObserverOptions['onChange']>();

  callbackRef.current = onChange;

  const frozen = state.entry?.isIntersecting && freezeOnceVisible;

  useEffect(() => {
    if (!ref || !('IntersectionObserver' in window) || frozen) {
      return;
    }

    let unobserve: (() => void) | undefined;

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]): void => {
        const thresholds = Array.isArray(observer.thresholds)
          ? observer.thresholds
          : [observer.thresholds];

        entries.forEach((entry) => {
          const isIntersecting =
            entry.isIntersecting &&
            thresholds.some(
              (threshold) => entry.intersectionRatio >= threshold
            );

          setState({ isIntersecting, entry });

          if (callbackRef.current) {
            callbackRef.current(isIntersecting, entry);
          }

          if (isIntersecting && freezeOnceVisible && unobserve) {
            unobserve();
            unobserve = undefined;
          }
        });
      },
      { threshold, root, rootMargin }
    );

    observer.observe(ref);

    return () => {
      observer.disconnect();
    };
  }, [ref, root, rootMargin, frozen, freezeOnceVisible, threshold]);

  const prevRef = useRef<Element | null>(null);

  useEffect(() => {
    if (
      !ref &&
      state.entry?.target &&
      !freezeOnceVisible &&
      !frozen &&
      prevRef.current !== state.entry.target
    ) {
      prevRef.current = state.entry.target;
      setState({ isIntersecting: initialIsIntersecting, entry: undefined });
    }
  }, [ref, state.entry, freezeOnceVisible, frozen, initialIsIntersecting]);

  const result = [
    setRef,
    !!state.isIntersecting,
    state.entry,
  ] as IntersectionReturn;

  result.ref = result[0];
  result.isIntersecting = result[1];
  result.entry = result[2];

  return result;
}
